import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { OrgChart } from 'd3-org-chart';
import * as d3 from 'd3';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import {
    Layout,
    Button,
    Input,
    Dropdown,
    Menu,
    Spin,
    App,
    AutoComplete,
    Drawer,
    Form,
    Select,
    Upload,
    Modal,
    Checkbox,
    Tabs,
    Row,
    Col,
    Result, // Added Result component
    Table
} from "antd";
import {
    SearchOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    CompressOutlined,
    DownloadOutlined,
    ExpandAltOutlined,
    FullscreenExitOutlined,
    EditOutlined,
    PlusOutlined,
    UserAddOutlined,
    TeamOutlined,
    DeleteOutlined,
    UploadOutlined,
    SaveOutlined,
    ExclamationCircleOutlined,
    MoreOutlined,
    DownOutlined,
    BarChartOutlined,
    UnorderedListOutlined,
    ArrowLeftOutlined, CloseOutlined, ExpandOutlined, ShrinkOutlined, UserOutlined, ReloadOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { tns as t } from '../../i18n/common';
import DepartmentEditDrawer from './Shared/DepartmentEditDrawer';
import TaskCreationModal from './TaskCreationModal';
import {
    normalizeRawUser,
    userDisplayName,
    buildImportWorkbook,
    buildReportWorkbook,
    writeWorkbook,
    isRemovalRow,
} from './Shared/orgExport';

const Organigram = () => {
    const { message } = App.useApp();
    const [modal, contextHolder] = Modal.useModal();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [permissionLoading, setPermissionLoading] = useState(true); // Permission loading state
    const [hasAccess, setHasAccess] = useState(false); // Permission access state
    const [chartData, setChartData] = useState([]);
    const [user, setUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [multiDeptUser, setMultiDeptUser] = useState(null);
    const [filteredRootId, setFilteredRootId] = useState(null); // Moved here to fix ReferenceError
    const [shouldExpandAll, setShouldExpandAll] = useState(false); // Flag to trigger expand all after filter clear
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);

    // Edit / Drawer State
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [currentNode, setCurrentNode] = useState(null); // The Org Node
    
    // Helper to get all unique users for "Existing User" dropdown and validation
    const getAllUsers = () => {
        const userMap = new Map();
        chartData.forEach(node => {
            if (node.lists) {
                ['leaders', 'mitarbeiter', 'viewers'].forEach(role => {
                    node.lists[role]?.forEach(u => {
                        if (!userMap.has(u.id)) {
                             userMap.set(u.id, {
                                 ...u,
                                 displayName: `${u.firstName} ${u.lastName}`
                             });
                        }
                    });
                });
            }
        });
        return Array.from(userMap.values()).sort((a, b) => a.lastName?.localeCompare(b.lastName));
    };

    // Users offered in the "Add existing user" dropdown. Merges the raw server
    // map (normalised) with users already in the chart (e.g. manually created
    // ones that don't exist in users.json), deduped by id. Both this list and
    // confirmAddUser's lookup MUST use the same sources — previously the
    // dropdown showed chart users while the handler only searched allRawUsers,
    // so picking a chart-only user silently did nothing.
    const getSelectableUsers = () => {
        const map = new Map();
        Object.values(allRawUsers || {}).forEach(raw => {
            if (raw.disabled || raw.isDisabled) return;
            const u = normalizeRawUser(raw);
            map.set(String(u.id), { ...u, displayName: userDisplayName(u) });
        });
        getAllUsers().forEach(u => {
            if (!map.has(String(u.id))) map.set(String(u.id), u);
        });
        return Array.from(map.values()).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));
    };

    // Helper to get all organizations for validation
    const getAllOrgs = () => {
        return chartData; 
    };

    // Helper to filter data based on root (Moved here to be available for renderChart)
    const getVisibleData = () => {
        if (!filteredRootId) return chartData;

        const rootIdStr = String(filteredRootId);
        // Build a set of all descendant IDs
        const descendants = new Set();
        const queue = [rootIdStr];
        
        while(queue.length > 0) {
            const currentId = queue.shift();
            descendants.add(currentId);
            
            // Find children in chartData (compare parentId as string)
            const children = chartData.filter(n => String(n.parentId) === currentId);
            children.forEach(child => queue.push(String(child.id)));
        }
        
        // Filter and CLONE data to avoid mutating original chartData
        // We must set the parentId of the new "root" to null, otherwise d3-org-chart 
        // might look for a missing parent and fail to render the hierarchy correctly.
        const filtered = chartData
            .filter(n => descendants.has(String(n.id)))
            .map(n => ({ ...n })); // Shallow copy object
            
        // Find the root in the new dataset and detach it from its parent
        const newRoot = filtered.find(n => String(n.id) === rootIdStr);
        if (newRoot) {
            newRoot.realParentId = newRoot.parentId; // Store real parent ID for "Up" button
            newRoot.parentId = null;
        }

        return filtered;
    };

    // Validators
    const checkUniqueEmail = (email, currentUserId) => {
        if(!email) return true;
        const allUsers = getAllUsers();
        return !allUsers.some(u => u.mail?.toLowerCase() === email.toLowerCase() && u.id !== currentUserId);
    };

    const checkUniqueUserExtId = (extId, currentUserId) => {
        if(!extId) return true;
        const allUsers = getAllUsers();
        return !allUsers.some(u => u.externalId === extId && u.id !== currentUserId);
    };

    const checkUniqueOrgExtId = (extId, currentOrgId) => {
        if(!extId) return true;
        const allOrgs = getAllOrgs();
        return !allOrgs.some(o => o.id === extId && o.id !== currentOrgId);
    };
    
    // Excel Import State
    const [pendingChanges, setPendingChanges] = useState([]);
    const [reviewModalVisible, setReviewModalVisible] = useState(false);
    const [selectedChanges, setSelectedChanges] = useState([]);
    
    // Change Log State
    const [changeLog, setChangeLog] = useState([]);
    
    // Language State
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');
    const [taskModalVisible, setTaskModalVisible] = useState(false);
    const [allRawUsers, setAllRawUsers] = useState({}); // Store raw users for export
    
    // Tooltip State
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });

    useEffect(() => {
        const handleLocaleChange = () => {
             const newLocale = localStorage.getItem('locale') || 'en';
             setLocale(newLocale);
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);

    // Initial Load
    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Check Permissions First
                const token = localStorage.getItem('token');
                if (!token) {
                    setPermissionLoading(false);
                    setHasAccess(false);
                    return;
                }

                if (import.meta.env.DEV) {
                    setHasAccess(true);
                } else {
                    try {
                        const azureToken = localStorage.getItem('azure_token');
                        const permResponse = await axios.get('/azure/permissions', {
                             headers: { 
                                 Authorization: `Bearer ${token}`,
                                 'X-Azure-Token': azureToken 
                             }
                        });
                        
                        if (permResponse.data && permResponse.data.modulePermissions && permResponse.data.modulePermissions.superuser === true) {
                            setHasAccess(true);
                        } else {
                            setHasAccess(false);
                            setPermissionLoading(false);
                            return; // Stop here if no access
                        }
                    } catch (permError) {
                        console.error("Permission check failed:", permError);
                        setHasAccess(false);
                        setPermissionLoading(false);
                        return;
                    }
                }
                
                setPermissionLoading(false); // Permissions checked and user has access

                // 2. Fetch User Data to get Tenant ID
                const userResponse = await axios.post('/user');
                setUser(userResponse.data);

                const tenantUUID = userResponse.data.azure_tenant ? userResponse.data.azure_tenant.azure_tenant_id : null;
                
                if (!tenantUUID) {
                    console.error("No Azure Tenant UUID found for user. Check if user belongs to a tenant.");
                    setLoading(false);
                    return;
                }

                // 3. Try to Load from Secure Storage FIRST
                let loadedFromStorage = false;
                try {
                     const { loadSecureData } = await import('../../utils/secureStorage');
                     const decrypted = await loadSecureData('org_data_cache');
                     const decryptedLog = await loadSecureData('org_changelog_cache');
                     
                     if (decryptedLog && Array.isArray(decryptedLog)) {
                         setChangeLog(decryptedLog);
                     }

                     if (decrypted && Array.isArray(decrypted)) {
                         setChartData(decrypted);
                         setLoading(false);
                         loadedFromStorage = true;
                     }
                } catch (storageErr) {
                    console.error("[Organigram] Failed to load secure storage:", storageErr);
                }

                if (loadedFromStorage) return;

                // 4. Fetch Data from API if no local storage
                const dataResponse = await axios.get(`/organigram/${tenantUUID}/data.json`);
                const userDataResponse = await axios.get(`/organigram/${tenantUUID}/users.json`);

                if (dataResponse.headers['last-modified']) {
                    const date = new Date(dataResponse.headers['last-modified']);
                    setLastUpdated(date.toLocaleString());
                }

                const rawData = dataResponse.data;
                const usersMap = userDataResponse.data;
                setAllRawUsers(usersMap); // Save for export usage

                // Process data locally (reconstruct structure)
                const processedData = processData(rawData, usersMap);
                setChartData(processedData);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
                setPermissionLoading(false);
            }
        };

        fetchData();
    }, []);

    // Keep a ref to the latest state so the unmount flush below always saves the
    // newest data without re-subscribing.
    const latestDataRef = useRef({ chartData, changeLog });
    useEffect(() => { latestDataRef.current = { chartData, changeLog }; }, [chartData, changeLog]);

    // Persist (encrypt -> IndexedDB) a given snapshot. Saves the two caches
    // sequentially so org data and the change log can never end up out of sync.
    const persistOrgData = async (cd, cl) => {
        try {
            const { saveSecureData } = await import('../../utils/secureStorage');
            // Create a clean copy to avoid circular references and D3 internals
            const cleanData = cd.map(node => {
                // eslint-disable-next-line no-unused-vars
                const { parent, children, depth, height, x, y, x0, y0, data, _children, ...rest } = node;
                return rest;
            });
            await saveSecureData('org_data_cache', cleanData);
            await saveSecureData('org_changelog_cache', cl);
            window.dispatchEvent(new Event('org-data-changed'));
        } catch (e) {
            console.error("[Organigram] Failed to save encrypted data:", e);
        }
    };

    // Effect to Save Data to Encrypted Storage on Change.
    // Proper debounce: clearing the timer on every change coalesces rapid edits
    // into ONE save of the latest state. The previous version returned an empty
    // cleanup, so each edit left its own timer running and the async encrypted
    // writes raced — an older snapshot could finish after a newer one and
    // overwrite it (lost changes / data-vs-changelog desync).
    useEffect(() => {
        if (loading || (chartData.length === 0 && changeLog.length === 0)) return;
        const timer = setTimeout(() => persistOrgData(chartData, changeLog), 1000);
        return () => clearTimeout(timer);
    }, [chartData, changeLog, loading]);

    // Flush the latest state once on unmount, so navigating away within the
    // debounce window still persists the final change.
    useEffect(() => {
        return () => {
            const { chartData: cd, changeLog: cl } = latestDataRef.current;
            if (cd.length > 0 || cl.length > 0) persistOrgData(cd, cl);
        };
    }, []);

    // Listener for Reset Event (triggered from Footer)
    useEffect(() => {
        const handleReset = () => {
             // Logic handled in footer (reload), but if we wanted to reset without reload:
             // localStorage.removeItem('org_data_cache');
             // fetchData(); // Re-fetch
        };
        window.addEventListener('reset-organigram', handleReset);
        return () => window.removeEventListener('reset-organigram', handleReset);
    }, []);

    // Bind global delete function for D3 HTML strings
    useEffect(() => {
        window.triggerOrgDelete = (id) => {
            deleteOrg(id);
        };
        return () => {
             delete window.triggerOrgDelete;
        };
    }, [chartData, changeLog]); // Depend on data/log state to ensure closure captures latest

    // Bind global tooltip handlers
    useEffect(() => {
        window.triggerOrgTooltip = (event, content) => {
            if (!event) return;
            const x = event.clientX;
            const y = event.clientY;
            setTooltip({ visible: true, x, y, content });
        };
        
        window.hideOrgTooltip = () => {
            setTooltip(prev => ({ ...prev, visible: false }));
        };

        return () => {
            delete window.triggerOrgTooltip;
            delete window.hideOrgTooltip;
        };
    }, []);


    const processData = (orgData, usersData) => {
        const flatData = [];

        const seenIds = new Set();
        const traverse = (node, inheritedParentId = null) => {
             // Filter out disabled departments and their entire subtrees
             if (node.enabled === false || node.enable === false) {
                 return 0;
             }

             // 1. Ensure Unique ID
             let uniqueId = node.id;
             if (seenIds.has(uniqueId)) {
                 let counter = 1;
                 while (seenIds.has(`${uniqueId}_${counter}`)) {
                     counter++;
                 }
                 uniqueId = `${uniqueId}_${counter}`;
             }
             seenIds.add(uniqueId);

            // Create normalized node
            const newNode = {
                ...node,
                id: uniqueId,
                // Use inherited parentId if available (structure-based), otherwise fallback to node's parent_id
                parentId: (inheritedParentId !== null) ? inheritedParentId : ((node.parent_id === "" || node.parent_id === null) ? null : node.parent_id),
                externalId: node.externalId || node.external_id,
                lists: {
                    leaders: node.leaders || [],
                    mitarbeiter: node.mitarbeiter || [],
                    viewers: node.viewers || []
                }
            };

            // Process users (mix embedded data with potential lookup)
            ['leaders', 'mitarbeiter', 'viewers'].forEach(role => {
                if (newNode.lists[role]) {
                    newNode.lists[role] = newNode.lists[role].reduce((acc, u) => {
                        // User data might be in usersData map or embedded
                        const fullUser = (usersData && usersData[u.id]) ? usersData[u.id] : u;
                        
                        // Filter out disabled users
                        // Check explicit disabled flags (keep enabled:false as they are just inactive/pending)
                        if (fullUser.disabled || fullUser.isDisabled) {
                            return acc;
                        }

                        // Determine display name: displayName or firstName + lastName
                        const firstName = fullUser.firstName || fullUser.givenName || '';
                        const lastName = fullUser.lastName || fullUser.surname || '';
                        const externalId = fullUser.externalUserId || fullUser.externalId || fullUser.external_id || u.externalUserId || u.externalId || u.external_id;
                        
                        acc.push({
                            ...u,
                            ...fullUser, // Merge full details
                            firstName,
                            lastName,
                            externalId, 
                            disabled: false // Since we filtered them out, they are not disabled properly speaking, but keeping structure.
                        });
                        return acc;
                    }, []);
                }
            });

            // Calculate Stats
            newNode.stats = {
                users: (newNode.lists.mitarbeiter?.length || 0) + (newNode.lists.leaders?.length || 0) + (newNode.lists.viewers?.length || 0),
                leaders: newNode.lists.leaders?.length || 0,
                mitarbeiter: newNode.lists.mitarbeiter?.length || 0,
                viewers: newNode.lists.viewers?.length || 0
            };

            // Add to flat list (without children array to avoid d3-org-chart confusion)
            const { children, ...nodeWithoutChildren } = newNode;
            flatData.push(nodeWithoutChildren); // Original push

            // Recurse & Calculate Descendants
            let subtreeCount = newNode.stats.users;

            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => {
                    subtreeCount += traverse(child, uniqueId); // Pass NEW unique ID as parent
                });
            }
            
            newNode.stats.descendants = subtreeCount - newNode.stats.users;
            newNode.stats.totalSubtree = subtreeCount;

            return subtreeCount;
        };

        if (Array.isArray(orgData)) {
            orgData.forEach(root => traverse(root));
        } else if (orgData) {
            traverse(orgData);
        }

        // --- Post-processing for Superiors (Export Data) ---
        const nodeMap = new Map();
        flatData.forEach(n => nodeMap.set(n.id, n));

        const getLeaders = (nodeId) => {
            const n = nodeMap.get(nodeId);
            return n?.lists?.leaders || [];
        };

        flatData.forEach(node => {
            const currentLeaders = node.lists.leaders || [];
            const parentLeaders = node.parentId ? getLeaders(node.parentId) : [];
            
            // Heuristic for Superior:
            // - For Employees/Viewers: The superior is the Leader of THIS node. 
            //   If no leader in this node, fallback to Parent Node's leader.
            // - For Leaders: The superior is the Leader of the PARENT node.

            const assignSup = (user, potentialSuperiors) => {
                if (potentialSuperiors && potentialSuperiors.length > 0) {
                    const sup = potentialSuperiors[0]; // Take first leader
                    user.derivedSupExternalId = sup.externalUserId || sup.externalId || "";
                    user.derivedSupFirstName = sup.firstName || "";
                    user.derivedSupLastName = sup.lastName || "";
                } else {
                    user.derivedSupExternalId = "";
                    user.derivedSupFirstName = "";
                    user.derivedSupLastName = "";
                }
            };

            // 1. Process Employees
            if (node.lists.mitarbeiter) {
                node.lists.mitarbeiter.forEach(u => {
                    assignSup(u, currentLeaders.length > 0 ? currentLeaders : parentLeaders);
                });
            }

            // 2. Process Viewers
            if (node.lists.viewers) {
                node.lists.viewers.forEach(u => {
                    assignSup(u, currentLeaders.length > 0 ? currentLeaders : parentLeaders);
                });
            }

            // 3. Process Leaders
            if (node.lists.leaders) {
                node.lists.leaders.forEach(u => {
                    assignSup(u, parentLeaders);
                });
            }
        });

        return flatData;
    };

    // Render Chart when data is ready
    useEffect(() => {
        if (!loading && chartData.length > 0 && chartContainerRef.current) {
            
            // Check for focusId from navigation state (Deep Link)
            let initialFit = true;
            let focusOptions = null;

            if (location.state?.focusId) {
                initialFit = false; // We will handle zoom manually
                const { focusId, type, searchName } = location.state;

                if (searchName) {
                    setSearchQuery(searchName);
                    // Trigger search logic to populate dropdown/results
                    handleSearch(searchName);
                }
                
                // Construct a mock option object compatible with onSelectSearchResult
                focusOptions = {
                    key: focusId,
                    type: type === 'user' ? 'user' : 'node',
                    nodeIds: [focusId],
                    label: searchName || ''
                };

                // If type is user, we need to find which nodes contain this user to start highlight
                if (type === 'user') {
                     const nodesWithUser = [];
                     chartData.forEach(node => {
                         if (node.lists) {
                             ['leaders', 'mitarbeiter', 'viewers'].forEach(role => {
                                 if (node.lists[role]?.some(u => u.id === focusId)) {
                                     nodesWithUser.push(node.id);
                                 }
                             });
                         }
                     });
                     if (nodesWithUser.length > 0) {
                         focusOptions.nodeIds = nodesWithUser;
                     }
                }
            }

            // Initial Render
            renderChart({ fit: initialFit });

            // Apply Focus if needed
            if (focusOptions) {
                // We use a small timeout to let the chart settle if needed, or just call directly.
                // d3-org-chart renders synchronously usually, but DOM updates might tick.
                setTimeout(() => {
                    onSelectSearchResult(null, focusOptions);
                }, 300);
            }
        }
    }, [loading, chartData, filteredRootId]);

    // Handle Window Resize / Container Resize
    useEffect(() => {
        if (!chartContainerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (chartData.length > 0 && chartRef.current) {
                 // Debounce could be added here if performance is an issue, 
                 // but for now direct re-render ensures immediate feedback.
                 // We simply re-render to recalculate layout based on new container dimensions.
                 // Preserve current transform if possible? For now, standard re-render.
                 renderChart({ fit: false }); // Don't reset zoom on resize
            }
        });

        resizeObserver.observe(chartContainerRef.current);

        return () => {
             resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const visibleData = getVisibleData();
        if (visibleData && visibleData.length > 0) {
            renderChart();
            
            // If triggered by "Expand All" and we just cleared the filter/re-rendered
            if (shouldExpandAll) {
                setTimeout(() => {
                    chartRef.current?.expandAll();
                    chartRef.current?.fit();
                    chartRef.current?.render();
                    setShouldExpandAll(false);
                }, 100); // Small delay to ensure render is complete
            }
        }
    }, [chartData, filteredRootId, shouldExpandAll]); // Re-render when data or filter changes

    const renderChart = (options = { fit: true }) => {
        if (!chartContainerRef.current) return;
        
        const visibleData = getVisibleData();
        if (!visibleData || visibleData.length === 0) {
             chartContainerRef.current.innerHTML = '';
             return;
        }

        // Clear previous content
        chartContainerRef.current.innerHTML = '';

        const calculateNodeHeight = (node) => {
            // Heuristic for Title Height based on character count (approx 30 chars per line with 320px width)
            const titleLen = node.name ? node.name.length : 0;
            let titleHeight = 50; // Base 1 line + padding
            if (titleLen > 28) titleHeight += 20; // 2 lines
            if (titleLen > 56) titleHeight += 20; // 3 lines
            if (titleLen > 84) titleHeight += 20; // 4 lines

            let height = titleHeight + 45 + 25; // Title + Stats Row (45) + Bottom Padding (25)
            
            const itemHeight = 36; // User item height (increased for safety)
            const sectionHeaderHeight = 30; // Section header + margin
            const sectionGap = 5;

            // Compact Mode for Containers (0 direct users)
            if (node.stats && node.stats.users === 0) {
                return 70 + (titleHeight - 50); // Adjust compact height if title is huge
            }

            if (node.lists) {
                ['leaders', 'mitarbeiter', 'viewers'].forEach(key => {
                    const list = node.lists[key];
                    if (list && list.length > 0) {
                        height += sectionHeaderHeight + (list.length * itemHeight) + sectionGap;
                    }
                });
            }
            return Math.max(140, height);
        };

        chartRef.current = new OrgChart();
        const chart = chartRef.current
            .container(chartContainerRef.current)
            .data(visibleData)
            .nodeId(d => d.id)
            .parentNodeId(d => d.parentId)
            .initialExpandLevel(filteredRootId ? 1 : 6)
            .nodeHeight(d => calculateNodeHeight(d.data))
            .nodeWidth(d => 320)
            .childrenMargin(d => 50)
            .compactMarginBetween(d => 35)
            .compactMarginPair(d => 30)
            .buttonContent(({ node }) => {
                return `<div style="width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; color:#716E7B; border-radius:50%; font-size:10px; margin:auto auto; background-color:white; border: 1px solid #E4E2E9; transform: translateZ(0); pointer-events: auto;"> <span style="font-size:9px; display: flex;">${
                    node.children
                        ? `<svg viewBox="0 0 320 512" width="10" height="10" fill="currentColor"><path d="M177 159.7l136 136c9.4 9.4 9.4 24.6 0 33.9l-22.6 22.6c-9.4 9.4-24.6 9.4-33.9 0L160 256.9 63.5 352.2c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l136-136c9.4-9.5 24.6-9.5 34 .1z"/></svg>`
                        : `<svg viewBox="0 0 320 512" width="10" height="10" fill="currentColor"><path d="M143 352.3L7 216.3c-9.4-9.4-9.4-24.6 0-33.9l22.6-22.6c9.4-9.4 24.6-9.4 33.9 0l96.5 96.5 96.5-96.5c9.4-9.4 24.6-9.4 33.9 0l22.6 22.6c9.4 9.4 9.4 24.6 0 33.9l-136 136c-9.2 9.4-24.4 9.4-33.8 0z"/></svg>`
                }</span> ${node.data._directSubordinates}  </div>`;
            })
            .onNodeClick(d => handleNodeClick(d))
            .nodeContent(function (d) {
                const node = d.data;
                const stats = node.stats || { users: 0, leaders: 0, mitarbeiter: 0, viewers: 0, descendants: 0 };

                // --- Up Button Logic (Shared) ---
                const isFilteredRoot = String(d.id) === String(filteredRootId);
                let upButtonHtml = '';
                if (isFilteredRoot && d.data.realParentId) {
                    upButtonHtml = `
                        <div onclick="event.stopPropagation(); window.handleGoToParent('${d.data.realParentId}')" 
                             style="position:absolute; top:-12px; left:50%; transform:translateX(-50%); 
                                    width:24px; height:24px; background:white; border:1px solid #E4E2E9; 
                                    border-radius:50%; display:flex; align-items:center; justify-content:center; 
                                    cursor:pointer; z-index:10; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                           <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#595959" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                               <line x1="12" y1="19" x2="12" y2="5"></line>
                               <polyline points="5 12 12 5 19 12"></polyline>
                           </svg>
                        </div>
                    `;
                }

                // --- Compact Mode for Containers (0 direct users) ---
                if (stats.users === 0) {
                     let compactBorderStyle = '1px dashed #cbd5e0';
                     let bgStyle = 'background: #fdfdfd;';
                     
                     // Highlight Override
                     if (d.data.highlight) {
                        compactBorderStyle = '3px solid #007bff';
                        bgStyle = 'background: white;';
                     }

                     const descendantCount = stats.descendants || 0;

                     return `
                        <div xmlns="http://www.w3.org/1999/xhtml" style="font-family:'Segoe UI', sans-serif; ${bgStyle} cursor: pointer; border-radius:8px; border:${compactBorderStyle}; padding:0; height:${d.height}px; width:${d.width}px; box-sizing:border-box; display:flex; flex-direction:column; justify-content:center; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">
                            ${upButtonHtml}
                            <div style="padding:10px 15px; display:flex; align-items:center; gap: 10px;">
                                <div style="flex-grow:1;">
                                    <div style="font-weight:700; font-size:14px; color:#2d3748; line-height:1.2; margin-bottom:2px;">
                                        ${node.name}
                                    </div>
                                    <div style="font-size:11px; color:#718096; display:flex; align-items:center; gap:5px;">
                                        <span style="background:#edf2f7; padding:1px 5px; border-radius:3px; font-weight:600; font-size:10px;">CONTAINER</span>
                                        ${descendantCount > 0 ? `
                                            <div style="display:flex; align-items:center;">
                                                <span>• ${t('common', 'organigram_ui.sub_users')}</span>
                                            </div>
                                        ` : '<span style="color:#e53e3e;">• Empty</span>'}
                                    </div>
                                </div>
                                ${node.externalId ? `<div style="font-size:10px; color:#cbd5e0;">#${node.externalId}</div>` : ''}
                                
                                ${descendantCount > 0 ? `
                                <div style="margin-right: 5px; position: static !important;">
                                    <svg onmouseenter="window.triggerOrgTooltip(event, '${t('common', 'organigram_ui.descendant_tooltip')}')" onmouseleave="window.hideOrgTooltip()" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: #a0aec0; cursor: help;"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                </div>
                                ` : ''}

                                <div onclick="event.stopPropagation(); window.triggerOrgDelete('${node.id}')" style="cursor:pointer; color:#cbd5e0; margin-left: 0; font-size: 12px;" onmouseover="this.style.color='#f5222d'" onmouseout="this.style.color='#cbd5e0'">
                                    <svg viewBox="0 0 448 512" width="10" height="10" fill="currentColor">
                                        <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                     `;
                }

                // --- Normal Mode ---
                // Removed overflow-y:auto to prevent scrolling
                let usersHtml = '<div class="users-list" style="padding:10px; flex-grow:1; background:white; display:flex; flex-direction:column; gap:24px;">';

                const renderSection = (title, list) => {
                    let html = '';
                    if (list && list.length > 0) {
                        html += `<div style="display:flex; flex-direction:column; gap:2px;">
                        <div style="font-size:11px; font-weight:700; color:#4a5568; text-transform:uppercase; letter-spacing:0.5px; border-bottom:1px solid #eee; padding-bottom:2px; margin-bottom:4px;">${title} (${list.length})</div>
                        <div style="display:flex; flex-direction:column; gap:2px;">`;
                        list.forEach(u => {
                            let baseStyle = 'display:block; font-size:12px; padding:2px 6px; border-radius:4px;';
                            
                            // Status Styles
                            if (u._changed) {
                                baseStyle += ' border: 1px solid #8e8e01; background-color: #f7ffc0ff; color: #8e8e01;';
                            } else if (u.disabled) {
                                baseStyle += ' color:#a0aec0; text-decoration:line-through; font-style:italic; background:#fff; border:1px solid #edf2f7;';
                            } else {
                                baseStyle += ' color:#2d3748; background:#f7fafc; border:1px solid #edf2f7;';
                            }

                            // Add External ID if present
                            const extIdHtml = u.externalId ? `<span style="color:#aaa; font-size:10px; margin-left: 4px;">(${u.externalId})</span>` : '';
                            
                            // Activation Status Indicator (Orange Circle) with Custom White Tooltip
                            let statusIcon = '';
                            if (u.enabled === false) {
                                const tooltipText = t('common', 'organigram_ui.user_not_activated');
                                statusIcon = `
                                    <div onmouseenter="window.triggerOrgTooltip(event, '${tooltipText}')" onmouseleave="window.hideOrgTooltip()" style="margin-left:auto; display:flex; align-items:center; cursor: help;">
                                        <div style="width: 8px; height: 8px; background-color: #fa8c16; border-radius: 50%;"></div>
                                    </div>
                                `;
                            }

                            // Use flex to align items and push status icon to right if present
                            baseStyle += ' display: flex; align-items: center; justify-content: space-between;';

                            html += `<span style="${baseStyle}">
                                        <span>${u.firstName} ${u.lastName} ${extIdHtml}</span>
                                        ${statusIcon}
                                     </span>`;
                        });
                        html += `</div></div>`;
                    }
                    return html;
                };

                if (node.lists) {
                    usersHtml += renderSection(t('common', 'organigram_ui.role_leaders'), node.lists.leaders);
                    usersHtml += renderSection(t('common', 'organigram_ui.role_mitarbeiter'), node.lists.mitarbeiter);
                    usersHtml += renderSection(t('common', 'organigram_ui.role_viewers'), node.lists.viewers);
                }

                usersHtml += '</div>';

                usersHtml += '</div>';
                
                // Detect Changes for Card Highlight
                let hasChanges = false;
                if (node.lists) {
                     ['leaders', 'mitarbeiter', 'viewers'].forEach(role => {
                         if(node.lists[role] && node.lists[role].some(u => u._changed)) hasChanges = true;
                     });
                }
                
                // Card Border Style
                // Priority: Search Highlight (Blue) > Change Highlight (Green) > Default (Gray)
                let borderStyle = '1px solid #e0e0e0';
                let cardEffect = '';
                
                if (d.data.highlight) {
                    borderStyle = '3px solid #007bff';
                    cardEffect = 'box-shadow: 0 0 20px rgba(0, 123, 255, 0.4);';
                } else if (hasChanges) {
                    borderStyle = '3px solid #007bff'; // Green
                    cardEffect = 'box-shadow: 0 0 25px rgba(0, 123, 255, 0.4);';
                }

                return `
            <div xmlns="http://www.w3.org/1999/xhtml" style="font-family:'Segoe UI', sans-serif; background-color:white; cursor: pointer; border-radius:8px; box-shadow:0 4px 8px rgba(0,0,0,0.1); border:${borderStyle}; ${cardEffect} padding:0; height:${d.height}px; width:${d.width}px; box-sizing:border-box; display:flex; flex-direction:column;">
                ${upButtonHtml}
                <div style="padding:12px; font-weight:800; font-size:15px; color:#1a202c; display:flex; align-items:center; background:#f8f9fa; border-bottom:2px solid #eaeaea; line-height:1.3;">
                    ${node.name}
                    ${node.externalId ? `<span style="margin-left:auto; font-weight:normal; color:#888; font-size:11px;">#${node.externalId}</span>` : ''}
                     <div onclick="event.stopPropagation(); window.triggerOrgDelete('${node.id}')" style="cursor:pointer; color:#ccc; margin-left: ${node.externalId ? '10px' : 'auto'}; font-size: 13px;" onmouseover="this.style.color='#f5222d'" onmouseout="this.style.color='#ccc'">
                        <svg viewBox="0 0 448 512" width="11" height="11" fill="currentColor">
                            <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"/>
                        </svg>
                    </div>
                </div>
                <div style="display:flex; border-bottom:1px solid #eee; background:white; padding:6px 0;">
                     <div style="flex:1; text-align:center; border-right:1px solid #eee;">
                        <span style="font-weight:bold; font-size:13px; color:#333; display:block;">${stats.users}</span>
                        <span style="font-size:10px; color:#777; text-transform:uppercase; letter-spacing:0.5px;">Total</span>
                     </div>
                     <div style="flex:1; text-align:center; border-right:1px solid #eee;">
                        <span style="font-weight:bold; font-size:13px; color:#333; display:block;">${stats.leaders}</span>
                        <span style="font-size:10px; color:#777; text-transform:uppercase; letter-spacing:0.5px;">L</span>
                     </div>
                     <div style="flex:1; text-align:center; ${stats.viewers > 0 ? 'border-right:1px solid #eee;' : ''}">
                        <span style="font-weight:bold; font-size:13px; color:#333; display:block;">${stats.mitarbeiter}</span>
                        <span style="font-size:10px; color:#777; text-transform:uppercase; letter-spacing:0.5px;">MA</span>
                     </div>
                     ${stats.viewers > 0 ? `
                     <div style="flex:1; text-align:center;">
                        <span style="font-weight:bold; font-size:13px; color:#333; display:block;">${stats.viewers}</span>
                        <span style="font-size:10px; color:#777; text-transform:uppercase; letter-spacing:0.5px;">View</span>
                     </div>` : ''}
                </div>
                ${usersHtml}
            </div>
            `;
            })
            .linkUpdate(function (d, i, arr) {
                d3.select(this)
                    .attr("stroke", "#404751") // Darker gray (User requested #404751ff, using #404751 for safety)
                    .attr("stroke-width", 1);  // Thicker line
            })
            .render();


        chartRef.current = chart;
        window.orgChart = chart; // Expose for debugging
        if (options.fit) {
            chart.fit(); // Fit the chart to view
        }
    };

    // Interactions
    const handleZoomIn = () => chartRef.current?.zoomIn();
    const handleZoomOut = () => chartRef.current?.zoomOut();
    const handleFit = () => chartRef.current?.fit();
    const handleExpandAll = () => { 
        if (filteredRootId) {
            // If filtered, clear filter first, then trigger expand all
            setFilteredRootId(null);
            setShouldExpandAll(true);
            message.info(t('common', 'organigram_ui.filter_cleared_for_expand')); // Optional feedback
        } else {
            chartRef.current?.expandAll(); 
            chartRef.current?.fit(); 
            chartRef.current?.render(); 
        }
    };
    const handleCollapseAll = () => { chartRef.current?.collapseAll(); chartRef.current?.render(); };

    // Department Search State
    // const [filteredRootId, setFilteredRootId] = useState(null); // Moved to top

    // Prepare Department Options
    const departmentOptions = React.useMemo(() => {
        if (!chartData || chartData.length === 0) return [];
        
        return chartData
            .filter(node => !node.isHelper) // Exclude helper nodes if any
            .map(node => {
                const leaders = node.lists?.leaders || [];
                const leaderName = leaders.length > 0 
                    ? `${leaders[0].firstName} ${leaders[0].lastName}` 
                    : t('common', 'organigram_ui.no_leader');
                
                return {
                    label: (
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                            <span style={{ fontWeight: 'bold' }}>{node.name}</span>
                            <span style={{ fontSize: '11px', color: '#888' }}>{leaderName}</span>
                        </div>
                    ),
                    value: node.id,
                    searchValue: `${node.name} ${leaderName}`, // For filtering by Dept or Leader
                    obj: node
                };
            })
            .sort((a, b) => a.searchValue.localeCompare(b.searchValue));
    }, [chartData]);
    
    // Bind navigation to window for template access
    useEffect(() => {
        window.handleGoToParent = (parentId) => {
            if (parentId) {
                setFilteredRootId(parentId);
            } else {
                setFilteredRootId(null);
            }
        };
        return () => {
           delete window.handleGoToParent;
        };
    }, []);

    const handleDepartmentSelect = (nodeId) => {
        setSearchQuery(null); // Clear search text
        
        if (!nodeId) {
            setFilteredRootId(null);
            return;
        }

        setFilteredRootId(nodeId);
        
        // After selecting, we technically want to "reset" the view to this node + 1 level
        // The renderChart effect will handle the data filtering and initial expansion.
    };

    const handleGoToParent = (parentIdOverride) => {
        if (!filteredRootId) return;
        
        // If passed directly (from node click), use it
        if (parentIdOverride && parentIdOverride !== 'null' && parentIdOverride !== 'undefined') {
             setFilteredRootId(parentIdOverride);
             return;
        }

        const currentNode = chartData.find(n => n.id === filteredRootId);
        if (currentNode && currentNode.parentId) {
            setFilteredRootId(currentNode.parentId);
        } else {
            // If no parent (root of whole org), clear filter
            setFilteredRootId(null);
        }
    };
    
    // Clear Filter
    const handleClearFilter = () => {
        setFilteredRootId(null);
    };

    // Helper to filter data based on root -> Moved to top
    
    // --- Restore Search Logic ---
    
    const handleSearch = (value) => {
        setSearchQuery(value);
        if (!value || value.length < 2) {
            setSearchResults([]);
            clearHighlights();
            return;
        }

        const queryLower = value.toLowerCase();
        const matches = [];
        const userMap = new Map(); // userId -> { value, label, type, nodeIds: [] }

        // Search within visible data if filtered, otherwise full data
        // User requested: "search functionality should work only on the visible card"
        const sourceData = filteredRootId ? getVisibleData() : chartData; 

        sourceData.forEach(node => {
            // Node Matches
            if (node.name.toLowerCase().includes(queryLower) || (node.externalId && node.externalId.toLowerCase().includes(queryLower))) {
                matches.push({ value: node.name, label: `${node.name} (Abteilung)`, type: 'node', key: node.id, nodeIds: [node.id] });
            }

            // User Matches
            if (node.lists) {
                ['leaders', 'mitarbeiter', 'viewers'].forEach(listKey => {
                    if (node.lists[listKey]) {
                        node.lists[listKey].forEach(u => {
                            const fullName = `${u.firstName} ${u.lastName}`;
                            // Determine display name
                            const displayName = fullName;
                            // Check if matches name OR external ID
                            const matchesName = displayName.toLowerCase().includes(queryLower);
                            const matchesExtId = u.externalId && String(u.externalId).toLowerCase().includes(queryLower);

                            if (matchesName || matchesExtId) {
                                if (!userMap.has(u.id)) {
                                    userMap.set(u.id, {
                                        value: displayName, // Show Name in Input
                                        label: displayName,
                                        key: u.id, // Unique Key
                                        type: 'user',
                                        nodeIds: []
                                    });
                                }
                                userMap.get(u.id).nodeIds.push(node.id);
                            }
                        });
                    }
                });
            }
        });

        // Convert userMap to array
        userMap.forEach(item => matches.push(item));

        setSearchResults(matches);
    };

    const findUserDepartments = (userId) => {
        const departments = [];
        if (!chartData) return departments;

        chartData.forEach(node => {
            if (node.lists) {
                ['leaders', 'mitarbeiter', 'viewers'].forEach(role => {
                    if (node.lists[role] && node.lists[role].some(u => u.id === userId)) {
                        departments.push({
                            nodeId: node.id,
                            nodeName: node.name,
                            role: role
                        });
                    }
                });
            }
        });
        return departments;
    };

    const onSelectSearchResult = (value, option) => {
        clearHighlights();
        
        let targetIds = [];
        if (option.type === 'user') {
            targetIds = option.nodeIds || [];
        } else if (option.type === 'node') {
            targetIds = [option.key]; 
        } else {
            targetIds = [option.key || value]; 
        }

        // Multi-department User Logic
        if (option.type === 'user') {
            const userId = option.key;
            const userDepts = findUserDepartments(userId);
            
            if (userDepts.length > 1) {
                setMultiDeptUser({
                    userId: userId,
                    name: option.label || option.value,
                    departments: userDepts
                });
            } else {
                setMultiDeptUser(null);
            }
        } else {
            // Only clear multiDeptUser if we are NOT navigating to one of the user's departments via the dropdown
            const isNavigatingToUserDept = multiDeptUser && multiDeptUser.departments.some(d => d.nodeId === (option.key || value));
            if (!isNavigatingToUserDept) {
                setMultiDeptUser(null);
            }
        }

        let found = false;
        targetIds.forEach(nid => {
            const node = chartData.find(d => d.id === nid);
            if (node) {
                node.highlight = true;
                found = true;
            }
        });

        if (found) {
            const chart = chartRef.current;
            if (chart) {
                const state = chart.getChartState();
                const allNodes = state.allNodes || chartData; 
                const expandedSet = new Set(state.expandedNodeIds || []);
                
                targetIds.forEach(targetId => {
                    let curr = allNodes.find(n => n.id === targetId || n.data?.id === targetId);
                    while (curr && curr.parent) {
                        expandedSet.add(curr.parent.id || curr.parent.data.id);
                        curr = curr.parent;
                    }
                });
                
                chart.setExpanded([...expandedSet]);
            }
            
            // Re-render (preserving current filter if possible, or we might need to auto-clear filter?)
            // If user searches for something OUTSIDE the filtered view, they won't see it.
            // Requirement decision: If search result is NOT in visible data, should we clear filter?
            // Let's check:
            const visibleIds = new Set(getVisibleData().map(n => n.id));
            const anyVisible = targetIds.some(id => visibleIds.has(id));
            
            if (!anyVisible && filteredRootId) {
                // If target is not visible, maybe we should warn or clear filter?
                // For now, let's auto-clear filter so they can jump to the result.
                setFilteredRootId(null);
                setFilteredRootId(null);
                message.info(t('common', 'organigram_ui.filter_cleared_for_search'));
            }

            // Render
            // We need to wait for state update if we just cleared filter.
            // But setFilteredRootId is async. 
            // In React 18, batching might help, but let's just trigger render chart logic.
            // Ideally rely on useEffect of [filteredRootId] but we also need to zoom.
            
            // Force a render cycle check?
            setTimeout(() => {
                 renderChart({ fit: false });
                 
                 setTimeout(() => {
                    zoomToNodes(targetIds);
                }, 500);
            }, 50);
        }
    };




    /**
     * Custom function to zoom and fit specific nodes.
     * Calculates the bounding box of the given node IDs and updates the chart transform.
     */
    const zoomToNodes = (nodeIds) => {
        const chart = chartRef.current;
        if (!chart) return;

        const state = chart.getChartState();
        const svg = state.svg;
        const container = chartContainerRef.current;
        
        if (!container) return;
        
        // Try DOM-based measurement first (Most robust against internal offsets/animations)
        // We use the container to select nodes to ensure we get what's on screen
        const targetNodes = d3.select(container).selectAll('.node').filter(function(d) {
             if (!d || !d.data) return false;
             return nodeIds.includes(d.data.id);
        });
        
        const foundCount = targetNodes.size();


        if (!targetNodes.empty()) {
             let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
             
             targetNodes.each(function(d) {
                 // Get screen (client) coordinates of the element
                  const rect = this.getBoundingClientRect();
                  minX = Math.min(minX, rect.left);
                  maxX = Math.max(maxX, rect.right);
                  minY = Math.min(minY, rect.top);
                  maxY = Math.max(maxY, rect.bottom);
             });
             
             // Check validity
             if (minX !== Infinity && minX !== 0 && maxX !== 0) { // Simple check to ensure we have real rects
                  // Container Rect
                  const cRect = container.getBoundingClientRect();
                  
                  // Calculate Screen Center of the bounds
                  const boundsCenterX = (minX + maxX) / 2;
                  const boundsCenterY = (minY + maxY) / 2;
                  
                  // Delta needed in Screen Coordinates
                  const dx = (cRect.left + cRect.width / 2) - boundsCenterX;
                  const dy = (cRect.top + cRect.height / 2) - boundsCenterY;
                  
                  // Use d3.zoomTransform of the svg node to get current k
                  // We need the ACTUAL D3 zoom behavior node.
                  // d3-org-chart typically binds zoom to the SVG or a wrapper G.
                  // state.svg is the selection.
                  const zoomNode = state.svg.node();
                  const currentTransform = d3.zoomTransform(zoomNode);
                  
                  let newScale = currentTransform.k;
                  
                  // Fit Logic
                  const boundsWidth = maxX - minX;
                  const boundsHeight = maxY - minY;
                  
                  if (nodeIds.length > 1) {
                      // Add abundant padding for multiple nodes to ensure they aren't edge-to-edge
                      const paddingX = 300; 
                      const paddingY = 300;
                      
                      const scaleX = cRect.width / (boundsWidth + paddingX);
                      const scaleY = cRect.height / (boundsHeight + paddingY);
                      newScale = Math.min(scaleX, scaleY);
                      
                      // Clamp
                      newScale = Math.min(newScale, 1.2); 
                      newScale = Math.max(newScale, 0.1); // Allow zooming out further for large spreads
                  } else {
                       newScale = 1.2; 
                  }
                  
                  // Logic: Center the BOX at scale `newScale`.
                  // We have `boundsCenterX` at `currentScale`.
                  // P_world = (boundsCenterX - currentTx) / currentK. Wait, simplified projection below:
                  
                  // Transform Logic:
                  // Target Point P (Box Center) MUST go to screen center (C).
                  // P_screen_current = boundsCenterX, boundsCenterY.
                  // P_world = transform.invert(P_screen_current).
                  // P_world_x = (boundsCenterX - currentTransform.x) / currentTransform.k;
                  // P_world_y = (boundsCenterY - currentTransform.y) / currentTransform.k;
                  
                  const pWorldX = (boundsCenterX - currentTransform.x) / currentTransform.k;
                  const pWorldY = (boundsCenterY - currentTransform.y) / currentTransform.k;
                  
                  // New Transform T2:
                  // T2(P_world) = C
                  // P_world * newScale + newTx = C
                  // newTx = C - P_world * newScale
                  
                  const finalTx = (cRect.width / 2) - pWorldX * newScale;
                  const finalTy = (cRect.height / 2) - pWorldY * newScale;
                  
                  // Apply
                  if (state.zoomBehavior) {
                      state.svg.transition().duration(750).call(
                          state.zoomBehavior.transform,
                          d3.zoomIdentity.translate(finalTx, finalTy).scale(newScale)
                      );
                  }
                  return;
             } else {
                 console.warn("zoomToNodes: DOM nodes found but invalid rects (0 dim?)");
             }
        } else {
             console.warn("zoomToNodes: No DOM nodes found. IDs might be hidden or mismatch.");
        }

        // FALLBACK: State-based logic (original implementation)
        console.warn("DOM-based zoom failed, falling back to state coordinates");
        // Access internal state to get node coordinates
        const allNodes = state.allNodes || [];
        
        const targets = allNodes.filter(n => nodeIds.includes(n.data.id));

        if (targets.length === 0) return;

        // Calculate Bounding Box
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

        targets.forEach(node => {
            // d3-org-chart nodes usually have x, y, width, height.
            // x and y seem to be center-top or center-middle based on library.
            // Inspection of library or assumption: d3-tree usually uses x,y as coordinates.
            // Width and Height are node sizes.
            // Let's assume (top-left) based on common rect usage, or convert.
            // Actually, d3-org-chart's default layout:
            // node.x is horizontal center.
            // node.y is top edge.
            
            const nX = node.x;
            const nY = node.y;
            const nW = node.width;
            const nH = node.height;

            minX = Math.min(minX, nX - nW / 2);
            maxX = Math.max(maxX, nX + nW / 2);
            minY = Math.min(minY, nY);
            maxY = Math.max(maxY, nY + nH);
        });

        // Check for valid bounds
        if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
             console.warn("Invalid node coordinates for zoom");
             return;
        }

        // Add Padding
        const padding = 50; 
        minX -= padding;
        maxX += padding;
        minY -= padding;
        maxY += padding;

        const boxWidth = maxX - minX;
        const boxHeight = maxY - minY;

        // Get SVG Dimension
        const svgWidth = chartContainerRef.current.clientWidth;
        const svgHeight = chartContainerRef.current.clientHeight;

        if (boxWidth <= 0 || boxHeight <= 0) return;

        // Calculate Scale
        const scaleX = svgWidth / boxWidth;
        const scaleY = svgHeight / boxHeight;
        let scale = Math.min(scaleX, scaleY);
        
        // Cap Scale (Don't zoom in TOO much for single nodes)
        scale = Math.min(scale, 1.2); 
        // Ensure not too small?
        scale = Math.max(scale, 0.1);

        // Center Point of the Bounding Box
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        // Apply Transform
        // We want (centerX, centerY) to be at (svgWidth/2, svgHeight/2)
        // Transform T: (x,y) -> (x*k + tx, y*k + ty)
        // We want T(centerX, centerY) = (W/2, H/2)
        // centerX*k + tx = W/2  => tx = W/2 - centerX*k
        
        const tx = svgWidth / 2 - centerX * scale;
        const ty = svgHeight / 2 - centerY * scale;

        if (state.svg) {
           state.svg.transition().duration(500).call(
               state.zoomBehavior.transform, 
               d3.zoomIdentity.translate(tx, ty).scale(scale)
           );
        }
    };

    const clearHighlights = () => {
        chartData.forEach(d => d.highlight = false);
        renderChart();
    };

    // --- DRAWER & EDIT LOGIC ---

    const handleNodeClick = (d) => {
        const node = chartData.find(n => n.id === d.data.id);
        if (node) {
            setCurrentNode(node);
            setDrawerVisible(true);
            //Shared drawer handles mode reset on open
        }
    };

    const handleAddUserClick = (role) => {
        // Shared component handles this internally
    };

    const handleEditUser = (user) => {
         // Shared component handles this internally
    };

    const closeDrawer = () => {
        setDrawerVisible(false);
        setCurrentNode(null);
    };

    // Builds (but does not store) a change-log entry. Kept separate from
    // logChange so callers like saveOrg can update an existing entry in place
    // instead of always appending a new one.
    const buildLogEntry = (type, key, params, details) => {
        // Enrich details with formatted diff if present
        let formattedDiff = '';
        if (details && details.diff) {
            formattedDiff = Object.entries(details.diff)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ');
        }

        // Lookup Parent Info for Export Context
        let parentOrgId = "";
        let parentOrgName = "";

        // Determine relevant Org ID
        let relatedNodeId = details?.orgId;
        // If not explicit, try to infer from currentNode if applicable
        if (!relatedNodeId && currentNode) relatedNodeId = currentNode.id;

        if (relatedNodeId && chartData) {
             const node = chartData.find(n => n.id === relatedNodeId);
             if (node && node.parentId) {
                 const parent = chartData.find(p => p.id === node.parentId);
                 if (parent) {
                     parentOrgId = parent.externalId || parent.external_id || "";
                     parentOrgName = parent.name || "";
                 }
             }
        }

        // Ensure we capture user context for export if available. firstName/lastName
        // here are a display fallback only — the authoritative snapshot lives in
        // details.user (spread below) so the admin table / Excel never guess.
        const exportContext = {
            id: details?.userId,
            firstName: params?.name?.split(' ')?.[0] || '', // Rough fallback
            lastName: params?.name?.split(' ')?.slice(1).join(' ') || '',
            parentOrgId,
            parentOrgName,
            ...details // details often contains 'user', 'org', 'role', etc.
        };

        return {
            id: Date.now() + Math.random().toString(36).substr(2, 9),
            type,
            key,
            params,
            timestamp: new Date().toISOString(),
            details: { ...details, formattedDiff, exportContext }
        };
    };

    const logChange = (type, key, params, details) => {
        setChangeLog(prev => [...prev, buildLogEntry(type, key, params, details)]);
    };

    const saveOrg = (values) => {
         if (!currentNode) return;
         // Values from form
         const updatedNode = { ...currentNode, ...values };
         
         // Update in chartData
         const newData = chartData.map(d => d.id === currentNode.id ? updatedNode : d);
         setChartData(newData);
         
         setCurrentNode(updatedNode);
         message.success(t('common', 'organigram_ui.details_saved'));

         // If this org was just added in this session, fold the rename / external
         // ID into the original "added" entry instead of logging a separate
         // update. Otherwise the record shows "Organisation hinzugefügt: New
         // Department" forever and you can't tell which department was created.
         setChangeLog(prev => {
             const addIdx = prev.findIndex(e => e.type === 'ORG_ADD' && e.details?.orgId === currentNode.id);
             if (addIdx >= 0) {
                 const copy = [...prev];
                 const e = copy[addIdx];
                 copy[addIdx] = {
                     ...e,
                     params: { ...e.params, name: values.name },
                     details: { ...e.details, externalId: values.externalId || '', finalName: values.name }
                 };
                 return copy;
             }
             return [...prev, buildLogEntry('ORG_UPDATE', 'logs.org_update', { name: values.name }, { orgId: currentNode.id, diff: values })];
         });

         triggerSaveWarning();
    };

    const saveUser = (updatedUser) => {
        // Shared component passes full updated user object
        let role = null;
        ['leaders', 'mitarbeiter', 'viewers'].forEach(r => {
            if (currentNode.lists[r]?.some(u => u.id === updatedUser.id)) role = r;
        });

        if (role) {
             const markedUser = { ...updatedUser, _changed: true };
             const updatedList = currentNode.lists[role].map(u => u.id === updatedUser.id ? markedUser : u);
             const updatedNode = { ...currentNode, lists: { ...currentNode.lists, [role]: updatedList } };

             // Update in chartData
             const newData = chartData.map(d => d.id === currentNode.id ? updatedNode : d);
             setChartData(newData);
             setCurrentNode(updatedNode);

             message.success(t('common', 'organigram_ui.user_updated'));
             logChange('USER_UPDATE', 'logs.user_update', { name: userDisplayName(updatedUser) }, { userId: updatedUser.id, diff: updatedUser, user: { ...updatedUser }, org: { id: currentNode.id, name: currentNode.name, externalId: currentNode.externalId }, role });
             triggerSaveWarning();
        }
    };

    const removeUser = (user) => {
        if (!user || !user.id || !currentNode) return;

        // Shared Drawer calls this on confirm
        let role = null;
        ['leaders', 'mitarbeiter', 'viewers'].forEach(r => {
            if (currentNode.lists[r]?.some(u => u.id === user.id)) role = r;
        });
        
        if (!role) return;

        // Defensive copy and filter
        const originalList = currentNode.lists[role] || [];
        const updatedList = originalList.filter(u => u.id !== user.id);
        
        // Deep clone lists to break any reference
        const updatedLists = { ...currentNode.lists, [role]: updatedList };
        const updatedNode = { ...currentNode, lists: updatedLists };

        // Update stats
        if(updatedNode.stats) {
             updatedNode.stats = { ...updatedNode.stats }; // Clone stats too
             updatedNode.stats[role] = Math.max(0, (updatedNode.stats[role] || 0) - 1);
             updatedNode.stats.users = Math.max(0, (updatedNode.stats.users || 0) - 1);
        }

        const newData = chartData.map(d => d.id === currentNode.id ? updatedNode : d);
        setChartData(newData);
        setCurrentNode(updatedNode); // Update Drawer
        
        renderChart({ fit: false });
        logChange('USER_REMOVE', 'logs.user_remove', { name: userDisplayName(user), org: currentNode.name, role: role }, { userId: user.id, orgId: currentNode.id, user: normalizeRawUser(user), org: { id: currentNode.id, name: currentNode.name, externalId: currentNode.externalId }, role });
        message.success(t('common', 'organigram_ui.user_removed'));
        triggerSaveWarning();
    };

    const confirmAddUser = (values, role) => {
        // Shared Drawer passes values and role
         let newUser;
        if (values.type === 'existing') {
            // Search the SAME merged pool the dropdown was built from (raw map +
            // chart users), with string-compared ids. Searching only allRawUsers
            // here made adding any chart-only user fail silently.
            const targetId = String(values.existingUserId);
            const existing = Object.values(allRawUsers || {}).find(u => String(u.id) === targetId)
                || getAllUsers().find(u => String(u.id) === targetId);
            if (!existing) {
                message.error(t('common', 'organigram_ui.user_not_found'));
                return;
            }
            // Raw users.json records use Azure field names (givenName/surname/
            // externalUserId). Normalise them or the user shows up as
            // "undefined undefined" in the chart, the drawer and the change log.
            newUser = normalizeRawUser(existing);
        } else {
             newUser = {
                id: "usr_" + Date.now(),
                firstName: values.firstName,
                lastName: values.lastName,
                externalId: values.externalId,
                jobTitle: values.jobTitle,
                mail: values.mail,
                language: values.language || 'DE',
                disabled: false
            };
        }
        newUser._changed = true; // highlight the addition in the chart

        if (!currentNode.lists[role]) currentNode.lists[role] = [];
        // Guard against double-adding: a duplicate id in the same role list
        // breaks React keys in the drawer and exports the user twice.
        if (currentNode.lists[role].some(u => String(u.id) === String(newUser.id))) {
            message.warning(t('common', 'organigram_ui.user_already_in_role'));
            return;
        }
        currentNode.lists[role].push(newUser);
        
        // Update stats
        if(!currentNode.stats) currentNode.stats = { users:0, leaders:0, mitarbeiter:0 };
        currentNode.stats[role] = (currentNode.stats[role] || 0) + 1;
        currentNode.stats.users++;

         // Update in chartData
         const newData = chartData.map(d => d.id === currentNode.id ? currentNode : d); // current node mutated above, safer to replace
         setChartData(newData);

        renderChart({ fit: false });
        setCurrentNode({ ...currentNode }); // Update Drawer
        logChange('USER_ADD', 'logs.user_add', { name: userDisplayName(newUser), org: currentNode.name, role: role }, { userId: newUser.id, orgId: currentNode.id, user: { ...newUser }, org: { id: currentNode.id, name: currentNode.name, externalId: currentNode.externalId }, role });
        triggerSaveWarning();
        message.success(t('common', 'organigram_ui.user_added'));
        setTimeout(() => {
            if (chartRef.current) {
                zoomToNodes([currentNode.id]);
            }
        }, 500);
    };

    const handleAddChildOrgClick = () => {
        // This functionality might need UI input? 
        // Or does it default to a placeholder?
        // Original code didn't show Add Child Org Modal... 
        // Ah, sorry, the previous code I replaced DID NOT have `renderDrawerContent` showing Add Child Org logic in `EDIT_ORG` mode...
        // Wait, line 1666 in my View File output SHOWED `handleAddChildOrgClick`.
        // I need to implement `confirmAddOrg`. 
        // But `DepartmentEditDrawer` creates the UI for editing existing.
        // For adding a child org, usually we ask for name/ID.
        // Does shared component support 'ADD_ORG' mode? No.
        // Organigram implementation seems to have been: Click button -> ???
        // Line 1666: `Button block ... onClick={handleAddChildOrgClick}`
        // And `handleAddChildOrgClick`... I don't see it defined in the snippet I viewed?
        // Ah, I missed viewing `handleAddChildOrgClick` function definition.
        // Assuming it opens a modal or changes drawer mode?
        // If it changes drawer mode to `ADD_ORG` (which I removed), then I need to handle it.
        // Since Shared Drawer is focused on EDITING current node and its users, adding a SUB org is different.
        // I will assume `handleAddChildOrgClick` opens a separate modal or I should implement a simple prompt here?
        // Or I can just pass a callback that uses `Modal.confirm` with input.
        
        // Let's implement it using Ant Design App/Modal for simplicity if not reusing drawer form.
        // Or, we can just say `confirmAddOrg` with default values for now if UI is missing.
        // NOTE: The previous `Organigram.jsx` code for `ADD_ORG` mode was seemingly missing or I missed viewing it?
        // No, `renderDrawerContent` had `if (drawerMode === 'EDIT_ORG')` ... and I see `confirmAddOrg` function in snippet.
        // But I don't see where `drawerMode` becomes `ADD_ORG`.
        // Maybe it uses a different mechanism.
        // Let's implement `handleAddChildOrgClick` to use a prompt or simple modal.
        
        // Actually, for now, let's use a Prompt or simple Modal to get name.
        // Or better, let's just make `DepartmentEditDrawer` handle `ADD_SUB_ORG` if we want consistency?
        // But for now to unblock, I will use a simple implementation:
        
        // Creating a temporary node and setting it as current node for drawer?
        // No, that would be "Editing" the new node. That's a valid strategy!
        // 1. Create new Node object
        // 2. Add to chartData
        // 3. Set as CurrentNode (Drawer opens in Edit Mode)
        // 4. User saves -> Name updated.
        
        const newId = "org_" + Date.now();
        const newOrg = {
            id: newId,
            parentId: currentNode.id,
            name: "New Department",
            externalId: "",
            enabled: true,
            stats: { users: 0, leaders: 0, mitarbeiter: 0 },
            lists: { leaders: [], mitarbeiter: [], viewers: [] },
            children: []
        };
        
        const newData = [...chartData, newOrg];
        setChartData(newData);
        setCurrentNode(newOrg); // Switch drawer to new node
        message.success(t('common', 'organigram_ui.org_added'));
        logChange('ORG_ADD', 'logs.org_add', { name: newOrg.name, parent: currentNode.name }, { orgId: newOrg.id, parentId: currentNode.id, parentName: currentNode.name });
        triggerSaveWarning();
        
        setTimeout(() => {
             if (chartRef.current) zoomToNodes([newId]);
        }, 500);
    };


    const deleteOrg = (orgId) => {
        const org = chartData.find(d => d.id === orgId);
        if (!org) return;

        // Safety Checks
        const hasUsers = (org.lists?.leaders?.length > 0) || (org.lists?.mitarbeiter?.length > 0) || (org.lists?.viewers?.length > 0);
        const hasChildren = chartData.some(d => d.parentId === orgId || d.pid === orgId || d.parent_id === orgId);

        if (hasUsers || hasChildren) {
             modal.error({
                 title: t('common', 'organigram_ui.delete_org'),
                 content: t('common', 'organigram_ui.delete_org_not_empty')
             });
             return;
        }

        modal.confirm({
            title: t('common', 'organigram_ui.delete_org_confirm'),
            content: org.name,
            okText: t('common', 'tasks.yes'),
            cancelText: t('common', 'tasks.no'),
            onOk: () => {
                const parentId = org.parentId;
                const newData = chartData.filter(d => d.id !== orgId);
                setChartData(newData);
                message.success(t('common', 'organigram_ui.org_deleted'));
                logChange('ORG_DELETE', 'logs.org_delete', { name: org.name }, { orgId: org.id });
                closeDrawer();
                triggerSaveWarning();
                setTimeout(() => {
                    if (chartRef.current && parentId) {
                        zoomToNodes([parentId]);
                    }
                }, 800);
            }
        });
    };


    // ...

    // Exports logic continued...
    // Re-importable file: same U101 columns so it still round-trips through
    // processExcelSync(), now with a Status column + colour marking layered on
    // (New = green, Updated = yellow, Removed = red/strikethrough). Deleted users
    // are re-inserted and marked rather than silently dropped.
    const exportExcel = () => {
        writeWorkbook(
            buildImportWorkbook({ chartData, allRawUsers, changeLog, t }),
            "U101_User_List.xlsx"
        );
    };

    // Human-readable task report: a "Changes" sheet describing exactly what was
    // changed, plus the marked user list.
    const exportReport = () => {
        writeWorkbook(
            buildReportWorkbook({ chartData, changeLog, t }),
            "Organigram_Changes_Report.xlsx"
        );
    };

    const exportPdf = () => {
        if (!chartRef.current) return;
        message.loading(t('common', 'organigram_ui.pdf_generating'), 0);
        
        chartRef.current.exportImg({
            full: true,
            save: false,
            scale: 10, // Higher scale for better quality
            onLoad: (base64) => {
                const img = new Image();
                img.src = base64;
                img.onload = () => {
                    const imgWidth = img.width;
                    const imgHeight = img.height;
                    const pdfWidth = imgWidth + 40; 
                    const pdfHeight = imgHeight + 40;

                    const pdf = new jsPDF({
                        orientation: imgWidth > imgHeight ? 'l' : 'p',
                        unit: 'px',
                        format: [pdfWidth, pdfHeight]
                    });
                    pdf.addImage(img, 'PNG', 20, 20, imgWidth, imgHeight);
                    pdf.save('organigram.pdf');
                    message.destroy();
                    message.success(t('common', 'organigram_ui.pdf_downloaded'));
                };
            }
        });
    };

    // Excel Import
    const handleExcelUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(firstSheet);
            processExcelSync(rows);
        };
        reader.readAsArrayBuffer(file);
        return false; // Prevent auto upload
    };

    const downloadChangeLog = () => {
        const content = changeLog.map(c => {
             const desc = c.key ? t('common', c.key, c.params) : (c.desc || '');
             return `[${c.timestamp}] ${c.type}: ${desc}`;
        }).join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'change_log.txt';
        a.click();
        URL.revokeObjectURL(url);
    };

    const downloadAll = () => {
        // The human report (with a Changes sheet) supersedes the old plain-text
        // change log; we also hand back the re-importable file.
        exportReport();
        setTimeout(() => {
            exportExcel();
        }, 500);
    };
    
    // Existing Save Warning State
    const [saveWarningVisible, setSaveWarningVisible] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    const triggerSaveWarning = () => {
        const hide = localStorage.getItem('organigram_hide_save_warning');
        if (hide !== 'true') {
            setSaveWarningVisible(true);
        }
    };

    const handleSaveWarningOk = () => {
        if (dontShowAgain) {
            localStorage.setItem('organigram_hide_save_warning', 'true');
        }
        setSaveWarningVisible(false);
    };

    const resetChanges = () => {
        Modal.confirm({
            title: t('common', 'organigram_ui.reset_confirm_title'),
            content: t('common', 'organigram_ui.reset_confirm_content'),
            okText: t('common', 'organigram_ui.reset'), 
            okType: 'danger',
            cancelText: t('common', 'organigram_ui.back'),
            onOk: async () => {
                try {
                    const { clearSecureData } = await import('../../utils/secureStorage');
                    await Promise.all([
                        clearSecureData('org_data_cache'),
                        clearSecureData('org_changelog_cache')
                    ]);
                    localStorage.removeItem('org_data_cache_flag');
                    window.location.reload();
                } catch (e) {
                    console.error("Reset failed", e);
                    window.location.reload(); // Fallback
                }
            }
        });
    };

    const processExcelSync = (rows) => {
        const changes = [];
        const allUsers = [];
        const allOrgs = [];
        const unknownOrgs = new Set(); // dept names referenced by the file but missing from the chart

        // Helper to gather all orgs and users
        const recurse = (nodes) => {
             nodes.forEach(n => {
                 allOrgs.push(n);
                 ['leaders', 'mitarbeiter', 'viewers'].forEach(role => {
                     if (n.lists && n.lists[role]) {
                         n.lists[role].forEach(u => allUsers.push({ u, org: n, role }));
                     }
                 });
                 if (n.children) recurse(n.children);
             });
        };
        recurse(chartData);

        // 1. Conflict Check & Superior ID Collection
        const idMap = new Map();
        const conflictingIds = new Set();
        const allSuperiorIds = new Set(); // Store IDs of people who are superiors

        rows.forEach(r => {
             const eid = r["Person ID"] ? String(r["Person ID"]).trim() : null;
             
             // Collect Superior ID
             const supId = r["Superior ID"] ? String(r["Superior ID"]).trim() : null;
             if(supId) allSuperiorIds.add(supId);

             if(eid) {
                 const name = (r["First Name"]||"").trim() + " " + (r["Last Name"]||"").trim();
                 if(idMap.has(eid) && idMap.get(eid) !== name) {
                      conflictingIds.add(eid);
                 } else {
                      idMap.set(eid, name);
                 }
             }
        });

        if(conflictingIds.size > 0) {
             message.warning(`Warning: ${conflictingIds.size} ID conflicts detected. Some rows might be skipped.`);
        }

        // 2. Process Rows
        rows.forEach(row => {
            const rawExtId = row["Person ID"];
            const extId = rawExtId ? String(rawExtId).trim() : null;
            const rawMail = row["E-Mail"];
            const mail = rawMail ? String(rawMail).trim().toLowerCase() : null;

            if(!extId && !mail) return;
            if(extId && conflictingIds.has(extId)) return;

            // 1. Find ALL instances of this user in current data
            const userInstances = allUsers.filter(x => {
                const uExt = x.u.externalId ? String(x.u.externalId).trim() : "";
                const uMail = x.u.mail ? String(x.u.mail).trim().toLowerCase() : "";
                return (extId && uExt === extId) || (!extId && mail && uMail === mail);
            });

            // Rows we previously exported as removals surface as REMOVE changes
            // (and are never re-added). This is what lets a re-import "see" the
            // deletions the client made.
            if (isRemovalRow(row)) {
                userInstances.filter(x => x.role !== 'viewers').forEach(inst => {
                    const dup = changes.find(c => c.type === 'REMOVE' && c.data.u.id === inst.u.id && c.data.org === inst.org);
                    if (!dup) {
                        changes.push({
                            type: 'REMOVE',
                            label: userDisplayName(inst.u),
                            desc: `Remove from ${inst.org.name}`,
                            data: { u: inst.u, org: inst.org, role: inst.role }
                        });
                    }
                });
                return;
            }

            // Ignore Viewers check (Pre-check)
            const validInstances = userInstances.filter(x => x.role !== 'viewers');

             // 2. Resolve Target Org
             const rawOrgExtId = row["Organisation ID"];
             const orgExtId = rawOrgExtId ? String(rawOrgExtId).trim() : null;
             const orgName = row["Organisation Name"] ? String(row["Organisation Name"]).trim() : null;
             
             let targetOrg = null;
             if (orgExtId) {
                 targetOrg = allOrgs.find(o => o.externalId && String(o.externalId).trim() === orgExtId);
             }
             if (!targetOrg && orgName) {
                 targetOrg = allOrgs.find(o => o.name && String(o.name).trim() === orgName);
             }
             // Row points at a department that doesn't exist in the current chart.
             // Such rows can't be placed and were previously dropped silently — track
             // them so we can tell the user instead of losing the change quietly.
             if (!targetOrg && orgName) {
                 unknownOrgs.add(orgName);
             }

             // Determine Role. Prefer the explicit "Ist Vorgesetzter" (is-manager)
             // column that our own export writes (J/N). Relying purely on whether
             // the ID appears as someone's "Superior ID" silently demotes a leader
             // who happens to have no subordinates in the file; fall back to that
             // inference only when the column is absent.
             const istVorgesetzter = String(row["Ist Vorgesetzter"] || '').trim().toUpperCase();
             let isLeader;
             if (['J', 'JA', 'Y', 'YES'].includes(istVorgesetzter)) {
                 isLeader = true;
             } else if (['N', 'NEIN', 'NO'].includes(istVorgesetzter)) {
                 isLeader = false;
             } else {
                 isLeader = extId && allSuperiorIds.has(extId);
             }
             const targetRole = isLeader ? 'leaders' : 'mitarbeiter';

             if (validInstances.length > 0) {
                 // CASE A: User exists (possibly multiple times). Check if they are in the Target Org.
                 
                 let matchingInstance = null;
                 
                 if (targetOrg) {
                     // Robust Match
                     const targetId = targetOrg.externalId ? String(targetOrg.externalId).trim() : null;
                     const targetName = targetOrg.name ? String(targetOrg.name).trim() : "";

                     matchingInstance = validInstances.find(x => {
                         const currentOrg = x.org;
                         const currentId = currentOrg.externalId ? String(currentOrg.externalId).trim() : null;
                         const currentName = currentOrg.name ? String(currentOrg.name).trim() : "";
                         
                         if (targetId) return currentId === targetId;
                         return currentName === targetName;
                     });
                 } else if (!orgExtId && orgName) {
                     // Fallback check by name
                     matchingInstance = validInstances.find(x => x.org.name === orgName);
                 }

                 if (matchingInstance) {
                      // ALREADY IN ORG -> UPDATE
                      const u = matchingInstance.u;
                      const newVals = {};
                      const desc = [];
                      
                      const newJob = row["Role"];
                      if(newJob && String(newJob).trim() !== String(u.jobTitle).trim()) {
                          desc.push(`Position: ${u.jobTitle} -> ${newJob}`);
                          newVals.jobTitle = newJob;
                      }
                      
                      // Language Mapping
                      const rawLang = row["Language"]; // e.g. "Deutsch", "English" or "DE"
                      if(rawLang) {
                          const langMapReverse = { 
                              'DEUTSCH': 'de', 'GERMAN': 'de', 'DE': 'de',
                              'ENGLISH': 'en', 'ENGLISCH': 'en', 'EN': 'en',
                              'FRANÇAIS': 'fr', 'FRENCH': 'fr', 'FR': 'fr',
                              'ITALIANO': 'it', 'ITALIAN': 'it', 'IT': 'it'
                          };
                          const upperLang = rawLang.toUpperCase();
                          const norm = langMapReverse[upperLang] || rawLang.substring(0, 2).toLowerCase();
                          
                          // Compare case-insensitively to avoid "DE" -> "de" spam
                          const currentLang = (u.preferredLanguage || u.language || '').toLowerCase();
                          if(norm && norm !== currentLang) {
                              desc.push(`Language: ${u.language} -> ${norm}`);
                              newVals.language = norm;
                              newVals.preferredLanguage = norm;
                          }
                      }
                      
                      // Check for Role Change (Promote/Demote)
                      // If user is currently mitarbeiter but Import says Leader (or vice versa)
                      if (matchingInstance.role !== targetRole) {
                           desc.push(`Role Change: ${matchingInstance.role} -> ${targetRole}`);
                           newVals._targetRole = targetRole; 
                      }

                      if(desc.length > 0) {
                          changes.push({
                              type: 'UPDATE',
                              label: `${u.firstName} ${u.lastName}`,
                              desc: desc.join(', '),
                              data: { u, org: matchingInstance.org, newVals, oldRole: matchingInstance.role }
                          });
                      }
                 } else {
                      // NOT IN TARGET ORG -> ADD ROLE (MOVE/ADD)
                      if (targetOrg) {
                           const canonicalUser = validInstances[0].u;
                           // Check if pending
                           const pending = changes.find(c => c.type === 'ADD_ROLE' && c.data.u.externalId === canonicalUser.externalId && c.data.org === targetOrg);
                           if(!pending) {
                                changes.push({
                                    type: 'ADD_ROLE',
                                    label: `${canonicalUser.firstName} ${canonicalUser.lastName}`,
                                    desc: `Add to ${targetOrg.name} as ${targetRole}`,
                                    data: { u: canonicalUser, org: targetOrg, role: targetRole }
                                });
                           }
                      }
                 }
             } else {
                 // CASE B: New User
                 if (targetOrg) {
                     const newName = (row["First Name"] || "") + " " + (row["Last Name"] || "");
                     const matchPending = changes.find(c => c.type === 'NEW_USER' && c.label === newName && c.data.org === targetOrg);
                     
                     if(!matchPending) {
                          // Clean Language
                          const rawLang = row["Language"] || "EN";
                          const langMapReverse = { 
                              'DEUTSCH': 'de', 'GERMAN': 'de', 'DE': 'de',
                              'ENGLISH': 'en', 'ENGLISCH': 'en', 'EN': 'en',
                              'FRANÇAIS': 'fr', 'FRENCH': 'fr', 'FR': 'fr',
                              'ITALIANO': 'it', 'ITALIAN': 'it', 'IT': 'it'
                          };
                          const upperLang = rawLang.toUpperCase();
                          const normLang = langMapReverse[upperLang] || rawLang.substring(0, 2).toLowerCase();

                          const newUser = {
                              id: "imp_" + Date.now() + "_" + Math.floor(Math.random()*1000),
                              firstName: row["First Name"] || "",
                              lastName: row["Last Name"] || "",
                              mail: row["E-Mail"] || "",
                              externalId: extId || "",
                              jobTitle: row["Role"] || "",
                              language: normLang,
                              disabled: false
                          };
                          changes.push({
                              type: 'NEW_USER',
                              label: `${newUser.firstName} ${newUser.lastName}`,
                              desc: `Create in ${targetOrg.name} as ${targetRole}`,
                              data: { user: newUser, org: targetOrg, role: targetRole }
                          });
                     }
                 }
             }
        });

        if (unknownOrgs.size > 0) {
            const names = Array.from(unknownOrgs).slice(0, 5).join(', ');
            message.warning(
                t('common', 'organigram_ui.import_unknown_orgs', {
                    count: unknownOrgs.size,
                    names: names + (unknownOrgs.size > 5 ? '…' : '')
                })
            );
        }

        if (changes.length > 0) {
            setPendingChanges(changes);
            setSelectedChanges(changes.map((_, i) => i)); // Select all by default
            setReviewModalVisible(true);
        } else if (unknownOrgs.size === 0) {
            message.info(t('common', 'organigram_ui.no_changes_excel'));
        }
    };
    
    const applyReviewChanges = () => {
        let appliedCount = 0;
        selectedChanges.sort((a,b) => a-b).forEach(idx => {
            const change = pendingChanges[idx];
            if(!change) return;

            if (change.type === 'UPDATE') {
                 const { u, org, newVals, oldRole } = change.data;
                 
                 // Handle explicit Role Change if present
                 if (newVals._targetRole && oldRole) {
                     const newRole = newVals._targetRole;
                     delete newVals._targetRole;
                     
                     // Move user: Remove from old list, add to new
                     if (org.lists[oldRole]) {
                         org.lists[oldRole] = org.lists[oldRole].filter(x => x.id !== u.id);
                         if(!org.stats) org.stats = {users:0, leaders:0, mitarbeiter:0, viewers:0};
                         org.stats[oldRole] = Math.max(0, (org.stats[oldRole]||0) - 1);
                     }
                     if (!org.lists[newRole]) org.lists[newRole] = [];
                     org.lists[newRole].push(u);
                     org.stats[newRole] = (org.stats[newRole]||0) + 1;
                 }

                 Object.assign(u, newVals);
                 u._changed = true;
                 logChange('EXCEL_UPDATE', 'logs.excel_update', { name: `${u.firstName} ${u.lastName}` }, { diff: newVals, user: u, org: org });
            } else if (change.type === 'ADD_ROLE') {
                 const { u, org, role } = change.data;
                 const targetRole = role || 'mitarbeiter'; 
                 if(!org.lists[targetRole]) org.lists[targetRole] = [];
                 if(!org.lists[targetRole].some(x => x.id === u.id)) {
                     org.lists[targetRole].push(u);
                     u._changed = true;
                     // Update stats
                     if(!org.stats) org.stats = {users:0, leaders:0, mitarbeiter:0, viewers:0};
                     org.stats[targetRole] = (org.stats[targetRole]||0) + 1;
                     org.stats.users++;
                     logChange('EXCEL_ADD_ROLE', 'logs.excel_add_role', { name: `${u.firstName} ${u.lastName}`, org: org.name }, { userId: u.id, user: u, org: org, role: targetRole });
                 }
            } else if (change.type === 'NEW_USER') {
                 const { user, org, role } = change.data;
                 const targetRole = role || 'mitarbeiter';
                 if(!org.lists[targetRole]) org.lists[targetRole] = [];
                 user._changed = true;
                 org.lists[targetRole].push(user);
                 if(!org.stats) org.stats = {users:0, leaders:0, mitarbeiter:0, viewers:0};
                 org.stats[targetRole] = (org.stats[targetRole]||0) + 1;
                 org.stats.users++;
                 logChange('EXCEL_NEW', 'logs.excel_new', { name: userDisplayName(user), org: org.name }, { userId: user.id, user: user, org: org, role: targetRole });
            } else if (change.type === 'REMOVE') {
                 const { u, org } = change.data;
                 const r = change.data.role || ['leaders','mitarbeiter','viewers'].find(x => org.lists?.[x]?.some(y => y.id === u.id));
                 if (r && org.lists?.[r]) {
                     org.lists[r] = org.lists[r].filter(x => x.id !== u.id);
                     if(org.stats) {
                         org.stats[r] = Math.max(0, (org.stats[r]||0) - 1);
                         org.stats.users = Math.max(0, (org.stats.users||0) - 1);
                     }
                     logChange('USER_REMOVE', 'logs.user_remove', { name: userDisplayName(u), org: org.name, role: r }, { userId: u.id, orgId: org.id, user: normalizeRawUser(u), org: { id: org.id, name: org.name, externalId: org.externalId }, role: r });
                 }
            }
            appliedCount++;
        });

        setPendingChanges([]);
        setSelectedChanges([]);
        setReviewModalVisible(false);
        renderChart();
        triggerSaveWarning();
        if(appliedCount > 0) {
            setChartData([...chartData]); // Force refresh
            message.success(t('common', 'organigram_ui.changes_applied', { count: appliedCount }));
        }
    };


    const menuItems = [
        { key: 'excel', label: 'Export Excel', onClick: exportExcel },
        { key: 'pdf', label: 'PDF herunterladen', onClick: exportPdf },
    ];

    if (permissionLoading) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 10 }}>Checking permissions...</div>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Result
                    status="403"
                    title="Access Denied"
                    subTitle="Sorry, you are not authorized to access this page. Superuser permission is required."
                    extra={<Button type="primary" href="/">Back Home</Button>}
                />
            </div>
        );
    }

    return (
        <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f4f4f9' }}>
            <style>{`
                .org-chart-container {
                    transform: translateZ(0); 
                    -webkit-transform: translateZ(0);
                }
                /* Fix foreignObject rendering in Safari */
                .org-chart-container svg foreignObject {
                    overflow: visible;
                }
            `}</style>
            {/* Header / Controls */}
            <div style={{ padding: '10px 20px', background: 'transparent', borderBottom: '1px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, zIndex: 10 }}>
                <div style={{ fontWeight: 'bold', fontSize: 18, display: 'flex', flexDirection: 'column' }}>
                    {/* {t('nav', 'organigram')} */}
                    {lastUpdated && (
                        <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666' }}>
                            {t('common', 'organigram_ui.last_update')} {lastUpdated}
                        </span>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                     {/* Filtered View Controls - Removed as per user request */}

                    <AutoComplete
                        style={{ width: 250, minWidth: 200, flexGrow: 1, maxWidth: 300 }}
                        value={searchQuery}
                        options={searchResults}
                        onSelect={onSelectSearchResult}
                        onSearch={handleSearch}
                        onChange={(val) => {
                            setSearchQuery(val);
                            if (!val) setMultiDeptUser(null);
                        }}
                    >
                        <Input 
                            suffix={<SearchOutlined />} 
                            placeholder={t("common", "search_placeholder")}
                            allowClear
                        />
                    </AutoComplete>

                    {/* Multi-Department User Selection */}
                    {multiDeptUser && (
                        <Select
                            placeholder="Select Department..."
                            style={{ width: 200, minWidth: 150 }}
                            onChange={(nodeId) => {
                                // Find the specific department entry
                                const dept = multiDeptUser.departments.find(d => d.nodeId === nodeId);
                                if (dept) {
                                    // Trigger selection via same handler, but specific node
                                    onSelectSearchResult(multiDeptUser.name, { 
                                        type: 'user', 
                                        key: multiDeptUser.userId, 
                                        label: multiDeptUser.name,
                                        nodeIds: [nodeId] // Target specific node
                                    });
                                }
                            }}
                            value={null} // Always reset after selection or show placeholder
                        >
                            {multiDeptUser.departments.map(d => (
                                <Select.Option key={d.nodeId} value={d.nodeId}>
                                    {d.nodeName} ({d.role})
                                </Select.Option>
                            ))}
                        </Select>
                    )}

                    <Select
                        showSearch
                        style={{ width: 250, minWidth: 200, flexGrow: 1, maxWidth: 300 }} // Responsive width
                        placeholder={t('common', 'organigram_ui.filter_department')}
                        filterOption={(input, option) =>
                            (option?.searchValue ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={handleDepartmentSelect}
                        value={filteredRootId} 
                        allowClear
                        options={departmentOptions}
                    />

                    <div style={{ display: 'flex', gap: 5 }}>
                        <Button onClick={handleFit} icon={<CompressOutlined />}>{t('common', 'fit_view')}</Button>
                        <Button onClick={handleExpandAll} icon={<ExpandAltOutlined />}>{t('common', 'expand_all')}</Button>
                    </div>
                    
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: 'insights',
                                    label: t('common', 'dashboard.nav.insights'),
                                    icon: <BarChartOutlined />,
                                    onClick: () => navigate('/organigram/insights')
                                },
                                {
                                    key: 'tasks',
                                    label: t('common', 'tasks.my_tasks'),
                                    icon: <UnorderedListOutlined />,
                                    onClick: () => navigate('/organigram/tasks')
                                },
                                {
                                    type: 'divider'
                                },
                                {
                                    key: '1',
                                    label: t('common', 'import_excel'),
                                    icon: <UploadOutlined />,
                                    onClick: () => document.getElementById('excel-upload').click()
                                },
                                {
                                    key: '2',
                                    label: t('common', 'export_excel'),
                                    icon: <DownloadOutlined />,
                                    onClick: exportExcel
                                },
                                {
                                    key: '3',
                                    label: t('common', 'export_pdf'),
                                    icon: <DownloadOutlined />,
                                    onClick: exportPdf
                                }
                            ]
                        }}
                    >
                        <Button>
                            {t('common', 'options')} <DownOutlined />
                        </Button>
                    </Dropdown>
                </div>
                <input
                    type="file"
                    id="excel-upload"
                    style={{ display: 'none' }}
                    accept=".xlsx, .xls"
                    onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                            handleExcelUpload(e.target.files[0]);
                        }
                        e.target.value = null; // Reset
                    }}
                />
            </div>


            {/* Chart Area */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
                 {loading ? (
                     <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 16 }}>
                         <Spin size="large" />
                         <div>Loading Organigram...</div>
                     </div>
                 ) : (
                    <div ref={chartContainerRef} className="org-chart-container" style={{ width: '100%', height: '100%', background: '#f4f4f9' }}></div>
                 )}

                 {/* Zoom Controls Overlay */}
                 <div style={{ position: 'absolute', bottom: 90, right: 30, display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <Button icon={<ZoomInOutlined />} onClick={handleZoomIn} />
                    <Button icon={<ZoomOutOutlined />} onClick={handleZoomOut} />
                 </div>
            </div>

            {/* Review Modal */}
            <Modal
                title={`${t('common', 'organigram_ui.review_changes_title')} (${pendingChanges.length})`}
                open={reviewModalVisible}
                onOk={applyReviewChanges}
                onCancel={() => setReviewModalVisible(false)}
                width={700}
                okText={t('common', 'organigram_ui.apply_selected')}
            >
                <div style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid #eee', padding: '5px', borderRadius: '4px' }}>
                     {pendingChanges.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>{t('common', 'organigram_ui.no_changes')}</div>}
                     
                     <Table
                        dataSource={pendingChanges.map((c, i) => ({ ...c, uniqueKey: i }))}
                        rowKey="uniqueKey"
                        pagination={false}
                        size="small"
                        rowSelection={{
                            selectedRowKeys: selectedChanges,
                            onChange: (selectedRowKeys) => setSelectedChanges(selectedRowKeys)
                        }}
                        columns={[
                            {
                                title: t('common', 'tasks.type_label') || 'Type',
                                dataIndex: 'type',
                                key: 'type',
                                filters: [...new Set(pendingChanges.map(c => c.type))].map(type => ({
                                    text: t('common', `types.${type.toLowerCase()}`) || type,
                                    value: type
                                })),
                                onFilter: (value, record) => record.type === value,
                                render: (type) => {
                                    let color = '#333';
                                    if(type === 'UPDATE') color = '#007bff';
                                    else if(type === 'ADD_ROLE') color = '#28a745';
                                    else if(type === 'NEW_USER') color = '#17a2b8';
                                    return <span style={{ color, fontWeight: 'bold' }}>{t('common', `types.${type.toLowerCase()}`) || type}</span>;
                                }
                            },
                            {
                                title: t('common', 'organigram_ui.column_changes', 'Changed Fields'),
                                key: 'column_changes',
                                filters: [...new Set(pendingChanges.flatMap(c => {
                                    if (c.type === 'UPDATE' && c.data.newVals) return Object.keys(c.data.newVals);
                                    if (c.type === 'ADD_ROLE') return ['role'];
                                    if (c.type === 'NEW_USER') return ['new_user'];
                                    return [];
                                }))].map(field => ({ text: field, value: field })),
                                onFilter: (value, record) => {
                                    if (record.type === 'UPDATE' && record.data.newVals) return Object.keys(record.data.newVals).includes(value);
                                    if (record.type === 'ADD_ROLE') return value === 'role';
                                    if (record.type === 'NEW_USER') return value === 'new_user';
                                    return false;
                                },
                                render: (_, record) => {
                                    if (record.type === 'UPDATE' && record.data.newVals) {
                                        const keys = Object.keys(record.data.newVals);
                                        return (
                                            <div style={{ fontSize: '12px' }}>
                                                {keys.length > 0 ? (
                                                    <span>
                                                        <b>{keys.length}</b> {keys.length === 1 ? 'change' : 'changes'}: {keys.slice(0, 3).join(', ')}{keys.length > 3 ? '...' : ''}
                                                    </span>
                                                ) : '-'}
                                            </div>
                                        );
                                    }
                                    return '-';
                                }
                            },
                            {
                                title: t('common', 'tasks.label_label') || 'Label',
                                dataIndex: 'label',
                                key: 'label',
                            },
                            {
                                title: t('common', 'tasks.description_label'),
                                dataIndex: 'desc',
                                key: 'desc',
                            }
                        ]}
                     />
                </div>
            </Modal>

            {/* Save Warning Modal */}
            <Modal
                title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ExclamationCircleOutlined style={{ color: '#faad14' }} /> {t('common', 'organigram_ui.save_warning_title')}</span>}
                open={saveWarningVisible}
                onOk={handleSaveWarningOk}
                onCancel={() => setSaveWarningVisible(false)}
                okText="OK"
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div dangerouslySetInnerHTML={{ __html: t('common', 'organigram_ui.save_warning_message') }} />
                    <Checkbox 
                        checked={dontShowAgain} 
                        onChange={(e) => setDontShowAgain(e.target.checked)}
                    >
                        {t('common', 'organigram_ui.dont_show_again')}
                    </Checkbox>
                </div>
            </Modal>

            {/* Department Edit Drawer (Shared) */}
            <DepartmentEditDrawer
                visible={drawerVisible}
                onClose={closeDrawer}
                node={currentNode}
                allUsers={getSelectableUsers()}
                locale={localStorage.getItem('locale') || 'de'}
                onSaveOrg={saveOrg}
                onAddUser={confirmAddUser}
                onUpdateUser={saveUser}
                onRemoveUser={removeUser}
                checkUniqueOrgExtId={checkUniqueOrgExtId}
                checkUniqueUserExtId={checkUniqueUserExtId}
                checkUniqueEmail={checkUniqueEmail}
                onAddChildOrg={handleAddChildOrgClick} 
                onDeleteOrg={deleteOrg}
            />

            {/* Change Notification Bar */}
            {changeLog.length > 0 && (
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, width: '100%',
                    background: '#ffffffff', color: 'white', padding: '10px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    zIndex: 900, boxSizing: 'border-box'
                }}>
                    <div style={{ color: '#000000' }}>
                        {t('common', 'organigram_ui.changes_count', { count: changeLog.length })}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button onClick={resetChanges}>{t('common', 'organigram_ui.reset')}</Button>
                        <Button onClick={downloadAll}>{t('common', 'organigram_ui.download_update')}</Button>
                        <Button type="primary"  onClick={() => setTaskModalVisible(true)}>{t('common', 'tasks.request_change')}</Button>
                    </div>
                </div>
            )}
            
            <TaskCreationModal
                visible={taskModalVisible}
                onClose={() => setTaskModalVisible(false)}
                changeLog={changeLog}
                chartData={chartData}
                allRawUsers={allRawUsers}
            />

            {/* Global Tooltip Portal */}
            {tooltip.visible && (
                <div style={{
                    position: 'fixed',
                    left: tooltip.x,
                    top: tooltip.y - 40, // Offset above cursor
                    transform: 'translateX(-50%)',
                    backgroundColor: 'white',
                    color: 'rgba(0, 0, 0, 0.85)',
                    padding: '6px 8px',
                    borderRadius: '2px',
                    boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                    zIndex: 9999,
                    fontSize: '12px',
                    pointerEvents: 'none',
                    whiteSpace: 'normal',
                    maxWidth: '200px',
                    lineHeight: '1.4'
                }}>
                   {tooltip.content}
                   {/* Arrow */}
                   <div style={{
                       position: 'absolute',
                       bottom: '-4px',
                       left: '50%',
                       transform: 'translateX(-50%) rotate(45deg)',
                       width: '8px',
                       height: '8px',
                       backgroundColor: 'white',
                       boxShadow: '2px 2px 2px -1px rgba(0, 0, 0, 0.05)' // Subtle shadow for depth
                   }}></div>
                </div>
            )}

            {contextHolder}
        </div>
    );
};

export default Organigram;
